import React, { useState, useEffect, useRef, useMemo } from "react";
import * as echarts from 'echarts';
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import {
  AdminPanelTitle,
  AdminMetric,
  AdminSupportingText,
  AdminLinkAction,
} from '../../sections/AdminDashboard/shared/adminUx';
import { AdminWidgetHeader, AdminWidgetPeriodFilter } from './AdminWidgetChrome';

// Stable data outside component to prevent identity changes on re-render
const DATA_BY_PERIOD = {
  '7 Days': [
    { title: 'Expert General Plumbing Solutions for Every Home', link: '/service-detail/?id=53', views: 12400, saves: 1400, shares: 184, isTop: true },
    { title: 'Shower Leak Repair in Melbourne and Geelong', link: '/service-detail/?id=30', views: 8900, saves: 980, shares: 156, isTop: false },
    { title: 'Leakage Plumbing Leakage Plumbing Leakage Plumbing', link: '/service-detail/?id=43', views: 5515, saves: 614, shares: 121, isTop: false }
  ],
  '14 Days': [
    { title: 'Leakage Plumbing Leakage Plumbing Leakage Plumbing', link: '/service-detail/?id=43', views: 24800, saves: 2800, shares: 368, isTop: true },
    { title: 'Expert General Plumbing Solutions for Every Home', link: '/service-detail/?id=53', views: 17800, saves: 1960, shares: 312, isTop: false },
    { title: 'Shower Leak Repair in Melbourne and Geelong', link: '/service-detail/?id=30', views: 11030, saves: 1228, shares: 242, isTop: false }
  ],
  '30 Days': [
    { title: 'Shower Leak Repair in Melbourne and Geelong', link: '/service-detail/?id=30', views: 14500, saves: 1600, shares: 210, isTop: true },
    { title: 'Leakage Plumbing Leakage Plumbing Leakage Plumbing', link: '/service-detail/?id=43', views: 11200, saves: 1200, shares: 180, isTop: false },
    { title: 'Expert General Plumbing Solutions for Every Home', link: '/service-detail/?id=53', views: 7600, saves: 820, shares: 140, isTop: false }
  ],
  '90 Days': [
    { title: 'Expert General Plumbing Solutions for Every Home', link: '/service-detail/?id=53', views: 13000, saves: 1500, shares: 190, isTop: true },
    { title: 'Shower Leak Repair in Melbourne and Geelong', link: '/service-detail/?id=30', views: 10100, saves: 1100, shares: 165, isTop: false },
    { title: 'Leakage Plumbing Leakage Plumbing Leakage Plumbing', link: '/service-detail/?id=43', views: 7000, saves: 750, shares: 135, isTop: false }
  ],
  '1 Year': [
    { title: 'Leakage Plumbing Leakage Plumbing Leakage Plumbing', link: '/service-detail/?id=43', views: 15000, saves: 1800, shares: 240, isTop: true },
    { title: 'Expert General Plumbing Solutions for Every Home', link: '/service-detail/?id=53', views: 12000, saves: 1400, shares: 200, isTop: false },
    { title: 'Shower Leak Repair in Melbourne and Geelong', link: '/service-detail/?id=30', views: 8200, saves: 900, shares: 150, isTop: false }
  ]
};

const TopPerformingPostWidget = ({
  className = 'masonry-item w-[420px] h-[630px] flex flex-col',
}) => {
  const [timeFilter, setTimeFilter] = useState('7 Days');
  const chartRefs = useRef([]);

  const topPosts = useMemo(() => DATA_BY_PERIOD[timeFilter] || DATA_BY_PERIOD['7 Days'], [timeFilter]);

  useEffect(() => {
    // For each post, (re)render 3 tiny bar charts
    topPosts.forEach((post, postIndex) => {
      const viewsDom = chartRefs.current[postIndex * 3];
      const savesDom = chartRefs.current[postIndex * 3 + 1];
      const sharesDom = chartRefs.current[postIndex * 3 + 2];

      if (!viewsDom || !savesDom || !sharesDom) return;

      const viewsChart = echarts.getInstanceByDom(viewsDom) || echarts.init(viewsDom);
      const savesChart = echarts.getInstanceByDom(savesDom) || echarts.init(savesDom);
      const sharesChart = echarts.getInstanceByDom(sharesDom) || echarts.init(sharesDom);

      const viewsOption = {
        grid: { top: 0, right: 0, bottom: 0, left: 0 },
        xAxis: { show: false, type: 'value', max: 15000 },
        yAxis: { show: false, type: 'category' },
        series: [{
          data: [post.views],
          type: 'bar',
          barWidth: '100%',
          itemStyle: { color: '#7B62D9', borderRadius: 4 },
          label: { show: false }
        }]
      };

      const savesOption = {
        grid: { top: 0, right: 0, bottom: 0, left: 0 },
        xAxis: { show: false, type: 'value', max: 2000 },
        yAxis: { show: false, type: 'category' },
        series: [{
          data: [post.saves],
          type: 'bar',
          barWidth: '100%',
          itemStyle: { color: '#03A62C', borderRadius: 4 },
          label: { show: false }
        }]
      };

      const sharesOption = {
        grid: { top: 0, right: 0, bottom: 0, left: 0 },
        xAxis: { show: false, type: 'value', max: 300 },
        yAxis: { show: false, type: 'category' },
        series: [{
          data: [post.shares],
          type: 'bar',
          barWidth: '100%',
          itemStyle: { color: '#F2C02D', borderRadius: 4 },
          label: { show: false }
        }]
      };

      viewsChart.setOption(viewsOption);
      savesChart.setOption(savesOption);
      sharesChart.setOption(sharesOption);
    });

    // Cleanup on filter change/unmount to prevent leaks
    return () => {
      topPosts.forEach((_, postIndex) => {
        const nodes = [
          chartRefs.current[postIndex * 3],
          chartRefs.current[postIndex * 3 + 1],
          chartRefs.current[postIndex * 3 + 2]
        ];
        nodes.forEach((node) => {
          const inst = node ? echarts.getInstanceByDom(node) : null;
          if (inst) inst.dispose();
        });
      });
    };
  }, [timeFilter]);

  return (
    <DashboardMainContainer className={className}>
      <AdminWidgetHeader title="Top Performing Post">
        <AdminWidgetPeriodFilter period={timeFilter} onPeriodChange={setTimeFilter} />
      </AdminWidgetHeader>
      
      <div className="flex-1 p-4 space-y-6">
        {topPosts.map((post, index) => (
          <div key={index} className="space-y-3">
            {/* Post Title */}
            <AdminLinkAction
              href={post.link}
              className="mb-4 block truncate no-underline hover:underline"
              title={post.title}
            >
              <AdminSupportingText className="text-offblack">{post.title}</AdminSupportingText>
            </AdminLinkAction>

            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-4">
              {/* Views */}
              <div className="text-left">
                <AdminMetric size="medium" className="text-offblack tabular-nums">
                  {(post.views / 1000).toFixed(1)}K
                </AdminMetric>
                <AdminPanelTitle className="text-offblack">Views</AdminPanelTitle>
              </div>
              
              {/* Saves */}
              <div className="text-left">
                <AdminMetric size="medium" className="text-offblack tabular-nums">
                  {(post.saves / 1000).toFixed(1)}K
                </AdminMetric>
                <AdminPanelTitle className="text-offblack">Saves</AdminPanelTitle>
              </div>
              
              {/* Shares */}
              <div className="text-left">
                <AdminMetric size="medium" className="text-offblack tabular-nums">
                  {post.shares}
                </AdminMetric>
                <AdminPanelTitle className="text-offblack">Shares</AdminPanelTitle>
              </div>
            </div>
            
            {/* Performance Bars in Single Row */}
            <div className="grid grid-rows-3 gap-2">
              <div ref={el => chartRefs.current[index * 3] = el} className="w-full h-3"></div>
              <div ref={el => chartRefs.current[index * 3 + 1] = el} className="w-full h-3"></div>
              <div ref={el => chartRefs.current[index * 3 + 2] = el} className="w-full h-3"></div>
            </div>
          </div>
        ))}
      </div>
    </DashboardMainContainer>
  );
};

export default TopPerformingPostWidget;
